import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { EarnSheet } from '../components/sheet/EarnSheet'
import { useEarnDashboardQuery } from '../hooks/api/useEarnDashboardQuery'
import { EARN_DASHBOARD_STATE, EarnAction, EarnAsset } from '../types'

interface EarnSheetContextValue {
  isSheetOpen: boolean
  earnSheetAction: EarnAction | null
  earnProducts: EarnAsset[]
  handleSheetOpen: (action: Omit<EarnAction, 'onClose'>) => void
  handleSheetUpdate: (action: Omit<EarnAction, 'onClose' | 'type'>) => void
}

const EarnSheetContext = createContext<EarnSheetContextValue | null>(null)

export const useEarnSheetContext = () => {
  const context = useContext(EarnSheetContext)

  if (!context) {
    throw new Error('useEarnSheetContext must be used within an EarnSheetProvider')
  }
  return context
}

export const EarnSheetProvider = ({ children }: { children: ReactNode }) => {
  const [earnSheetAction, setEarnSheetAction] = useState<EarnAction | null>(null)
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const { data: earnDashboardData } = useEarnDashboardQuery()

  const earnProducts = useMemo(
    () =>
      earnDashboardData?.state === EARN_DASHBOARD_STATE.READY ? earnDashboardData.earnAssets : [],
    [earnDashboardData]
  )

  const handleSheetOpen = useCallback((action: Omit<EarnAction, 'onClose'>) => {
    setEarnSheetAction({ ...action, onClose: () => setIsSheetOpen(false) })
    setIsSheetOpen(true)
  }, [])

  const handleSheetUpdate = useCallback((action: Omit<EarnAction, 'onClose' | 'type'>) => {
    setEarnSheetAction(prev =>
      prev ? { ...prev, ...action, onClose: () => setIsSheetOpen(false) } : prev
    )
  }, [])

  const contextValue = useMemo<EarnSheetContextValue>(
    () => ({ isSheetOpen, earnSheetAction, earnProducts, handleSheetOpen, handleSheetUpdate }),
    [isSheetOpen, earnSheetAction, earnProducts, handleSheetOpen, handleSheetUpdate]
  )

  return (
    <EarnSheetContext.Provider value={contextValue}>
      {children}
      <EarnSheet isSheetOpen={isSheetOpen} />
    </EarnSheetContext.Provider>
  )
}
